import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p><inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` is automatically generated for any operations where npm
modifies either the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` tree, or `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`. It describes the
exact tree that was generated, such that subsequent installs are able to
generate identical trees, regardless of intermediate dependency updates.`}</p>
    <p>{`This file is intended to be committed into source repositories, and serves
various purposes:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Describe a single representation of a dependency tree such that
teammates, deployments, and continuous integration are guaranteed to
install exactly the same dependencies.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Provide a facility for users to "time-travel" to previous states of
`}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` without having to commit the directory itself.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Facilitate greater visibility of tree changes through readable source
control diffs.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Optimize the installation process by allowing npm to skip repeated
metadata resolutions for previously-installed packages.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`As of npm v7, lockfiles include enough information to gain a complete
picture of the package tree, reducing the need to read `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`
files, and allowing for significant performance improvements.`}</p>
      </li>
    </ul>
    <h3><inlineCode parentName="h3">{`package-lock.json`}</inlineCode>{` vs `}<inlineCode parentName="h3">{`npm-shrinkwrap.json`}</inlineCode></h3>
    <p>{`Both of these files have the same format, and perform similar functions in
the root of a project.`}</p>
    <p>{`The difference is that `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` cannot be published, and it will
be ignored if found in any place other than the root project.`}</p>
    <p>{`In contrast, `}<a parentName="p" {...{
        "href": "/cli/v8/configuring-npm/npm-shrinkwrap-json"
      }}>{`npm-shrinkwrap.json`}</a>{` allows
publication, and defines the dependency tree from the point encountered.
This is not recommended unless deploying a CLI tool or otherwise using the
publication process for producing production packages.`}</p>
    <p>{`If both `}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` and `}<inlineCode parentName="p">{`npm-shrinkwrap.json`}</inlineCode>{` are present in the
root of a project, `}<inlineCode parentName="p">{`npm-shrinkwrap.json`}</inlineCode>{` will take precedence and
`}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{` will be ignored.`}</p>
    <h3>{`Hidden Lockfiles`}</h3>
    <p>{`In order to avoid processing the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` folder repeatedly, npm as
of v7 uses a "hidden" lockfile present in
`}<inlineCode parentName="p">{`node_modules/.package-lock.json`}</inlineCode>{`.  This contains information about the
tree, and is used in lieu of reading the entire `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` hierarchy
provided that the following conditions are met:`}</p>
    <ul>
      <li parentName="ul">{`All package folders it references exist in the `}<inlineCode parentName="li">{`node_modules`}</inlineCode>{` hierarchy.`}</li>
      <li parentName="ul">{`No package folders exist in the `}<inlineCode parentName="li">{`node_modules`}</inlineCode>{` hierarchy that are not
listed in the lockfile.`}</li>
      <li parentName="ul">{`The modified time of the file is at least as recent as all of the package
folders it references.`}</li>
    </ul>
    <p>{`That is, the hidden lockfile will only be relevant if it was created as
part of the most recent update to the package tree.  If another CLI mutates
the tree in any way, this will be detected, and the hidden lockfile will be
ignored.`}</p>
    <p>{`Note that it `}<em parentName="p">{`is`}</em>{` possible to manually change the `}<em parentName="p">{`contents`}</em>{` of a package
in such a way that the modified time of the package folder is unaffected.
For example, if you add a file to `}<inlineCode parentName="p">{`node_modules/foo/lib/bar.js`}</inlineCode>{`, then the
modified time on `}<inlineCode parentName="p">{`node_modules/foo`}</inlineCode>{` will not reflect this change.  If you
are manually editing files in `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{`, it is generally best to
delete the file at `}<inlineCode parentName="p">{`node_modules/.package-lock.json`}</inlineCode>{`.`}</p>
    <p>{`As the hidden lockfile is ignored by older npm versions, it does not
contain the backwards compatibility affordances present in "normal"
lockfiles.  That is, it is `}<inlineCode parentName="p">{`lockfileVersion: 3`}</inlineCode>{`, rather than
`}<inlineCode parentName="p">{`lockfileVersion: 2`}</inlineCode>{`.`}</p>
    <h3>{`Handling Old Lockfiles`}</h3>
    <p>{`When npm detects a lockfile from npm v6 or before during the package
installation process, it is automatically updated to fetch missing
information from either the `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` tree or (in the case of empty
`}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` trees or very old lockfile formats) the npm registry.`}</p>
    <h3>{`File Format`}</h3>
    <h4><inlineCode parentName="h4">{`name`}</inlineCode></h4>
    <p>{`The name of the package this is a package-lock for. This will match what's
in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.`}</p>
    <h4><inlineCode parentName="h4">{`version`}</inlineCode></h4>
    <p>{`The version of the package this is a package-lock for. This will match
what's in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`.`}</p>
    <h4><inlineCode parentName="h4">{`lockfileVersion`}</inlineCode></h4>
    <p>{`An integer version, starting at `}<inlineCode parentName="p">{`1`}</inlineCode>{` with the version number of this
document whose semantics were used when generating this
`}<inlineCode parentName="p">{`package-lock.json`}</inlineCode>{`.`}</p>
    <p>{`Note that the file format changed significantly in npm v7 to track
information that would have otherwise required looking in `}<inlineCode parentName="p">{`node_modules`}</inlineCode>{` or
the npm registry.  Lockfiles generated by npm v7 will contain
`}<inlineCode parentName="p">{`lockfileVersion: 2`}</inlineCode>{`.`}</p>
    <ul>
      <li parentName="ul">{`No version provided: an "ancient" shrinkwrap file from a version of npm
prior to npm v5.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`1`}</inlineCode>{`: The lockfile version used by npm v5 and v6.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`2`}</inlineCode>{`: The lockfile version used by npm v7, which is backwards compatible
to v1 lockfiles.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`3`}</inlineCode>{`: The lockfile version used by npm v7, `}<em parentName="li">{`without`}</em>{` backwards
compatibility affordances.  This is used for the hidden lockfile at
`}<inlineCode parentName="li">{`node_modules/.package-lock.json`}</inlineCode>{`, and will likely be used in a future
version of npm, once support for npm v6 is no longer relevant.`}</li>
    </ul>
    <p>{`npm will always attempt to get whatever data it can out of a lockfile, even
if it is not a version that it was designed to support.`}</p>
    <h4><inlineCode parentName="h4">{`packages`}</inlineCode></h4>
    <p>{`This is an object that maps package locations to an object containing the
information about that package.`}</p>
    <p>{`The root project is typically listed with a key of `}<inlineCode parentName="p">{`""`}</inlineCode>{`, and all other
packages are listed with their relative paths from the root project folder.`}</p>
    <p>{`Package descriptors have the following fields:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`version: The version found in `}<inlineCode parentName="p">{`package.json`}</inlineCode></p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`resolved: The place where the package was actually resolved from.  In
the case of packages fetched from the registry, this will be a url to a
tarball.  In the case of git dependencies, this will be the full git url
with commit sha.  In the case of link dependencies, this will be the
location of the link target. `}<inlineCode parentName="p">{`registry.npmjs.org`}</inlineCode>{` is a magic value meaning
"the currently configured registry".`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`integrity: A `}<inlineCode parentName="p">{`sha512`}</inlineCode>{` or `}<inlineCode parentName="p">{`sha1`}</inlineCode>{` `}<a parentName="p" {...{
            "href": "https://w3c.github.io/webappsec/specs/subresourceintegrity/"
          }}>{`Standard Subresource
Integrity`}</a>{`
string for the artifact that was unpacked in this location.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`link: A flag to indicate that this is a symbolic link.  If this is
present, no other fields are specified, since the link target will also
be included in the lockfile.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`dev, optional, devOptional: If the package is strictly part of the
`}<inlineCode parentName="p">{`devDependencies`}</inlineCode>{` tree, then `}<inlineCode parentName="p">{`dev`}</inlineCode>{` will be true.  If it is strictly part
of the `}<inlineCode parentName="p">{`optionalDependencies`}</inlineCode>{` tree, then `}<inlineCode parentName="p">{`optional`}</inlineCode>{` will be set.  If it
is both a `}<inlineCode parentName="p">{`dev`}</inlineCode>{` dependency `}<em parentName="p">{`and`}</em>{` an `}<inlineCode parentName="p">{`optional`}</inlineCode>{` dependency of a non-dev
dependency, then `}<inlineCode parentName="p">{`devOptional`}</inlineCode>{` will be set.  (An `}<inlineCode parentName="p">{`optional`}</inlineCode>{` dependency of
a `}<inlineCode parentName="p">{`dev`}</inlineCode>{` dependency will have both `}<inlineCode parentName="p">{`dev`}</inlineCode>{` and `}<inlineCode parentName="p">{`optional`}</inlineCode>{` set.)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`inBundle: A flag to indicate that the package is a bundled dependency.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`hasInstallScript: A flag to indicate that the package has a `}<inlineCode parentName="p">{`preinstall`}</inlineCode>{`,
`}<inlineCode parentName="p">{`install`}</inlineCode>{`, or `}<inlineCode parentName="p">{`postinstall`}</inlineCode>{` script.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`hasShrinkwrap: A flag to indicate that the package has an
`}<inlineCode parentName="p">{`npm-shrinkwrap.json`}</inlineCode>{` file.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`bin, license, engines, dependencies, optionalDependencies: fields from
`}<inlineCode parentName="p">{`package.json`}</inlineCode></p>
      </li>
    </ul>
    <h4>{`dependencies`}</h4>
    <p>{`Legacy data for supporting versions of npm that use `}<inlineCode parentName="p">{`lockfileVersion: 1`}</inlineCode>{`.
This is a mapping of package names to dependency objects.  Because the
object structure is strictly hierarchical, symbolic link dependencies are
somewhat challenging to represent in some cases.`}</p>
    <p>{`npm v7 ignores this section entirely if a `}<inlineCode parentName="p">{`packages`}</inlineCode>{` section is present,
but does keep it up to date in order to support switching between npm v6
and npm v7.`}</p>
    <p>{`Dependency objects have the following fields:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`version: a specifier that varies depending on the nature of the package,
and is usable in fetching a new copy of it.`}</p>
        <ul parentName="li">
          <li parentName="ul">{`bundled dependencies: Regardless of source, this is a version number
that is purely for informational purposes.`}</li>
          <li parentName="ul">{`registry sources: This is a version number. (eg, `}<inlineCode parentName="li">{`1.2.3`}</inlineCode>{`)`}</li>
          <li parentName="ul">{`git sources: This is a git specifier with resolved committish. (eg,
`}<inlineCode parentName="li">{`git+https://example.com/foo/bar#115311855adb0789a0466714ed48a1499ffea97e`}</inlineCode>{`)`}</li>
          <li parentName="ul">{`http tarball sources: This is the URL of the tarball. (eg,
`}<inlineCode parentName="li">{`https://example.com/example-1.3.0.tgz`}</inlineCode>{`)`}</li>
          <li parentName="ul">{`local tarball sources: This is the file URL of the tarball. (eg
`}<inlineCode parentName="li">{`file:///opt/storage/example-1.3.0.tgz`}</inlineCode>{`)`}</li>
          <li parentName="ul">{`local link sources: This is the file URL of the link. (eg
`}<inlineCode parentName="li">{`file:libs/our-module`}</inlineCode>{`)`}</li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li">{`integrity: A `}<inlineCode parentName="p">{`sha512`}</inlineCode>{` or `}<inlineCode parentName="p">{`sha1`}</inlineCode>{` `}<a parentName="p" {...{
            "href": "https://w3c.github.io/webappsec/specs/subresourceintegrity/"
          }}>{`Standard Subresource
Integrity`}</a>{`
string for the artifact that was unpacked in this location.  For git
dependencies, this is the commit sha.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`resolved: For registry sources this is path of the tarball relative to
the registry URL.  If the tarball URL isn't on the same server as the
registry URL then this is a complete URL. `}<inlineCode parentName="p">{`registry.npmjs.org`}</inlineCode>{` is a magic
value meaning "the currently configured registry".`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`bundled:  If true, this is the bundled dependency and will be installed
by the parent module.  When installing, this module will be extracted
from the parent module during the extract phase, not installed as a
separate dependency.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`dev: If true then this dependency is either a development dependency ONLY
of the top level module or a transitive dependency of one.  This is false
for dependencies that are both a development dependency of the top level
and a transitive dependency of a non-development dependency of the top
level.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`optional: If true then this dependency is either an optional dependency
ONLY of the top level module or a transitive dependency of one.  This is
false for dependencies that are both an optional dependency of the top
level and a transitive dependency of a non-optional dependency of the top
level.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`requires: This is a mapping of module name to version.  This is a list of
everything this module requires, regardless of where it will be
installed.  The version should match via normal matching rules a
dependency either in our `}<inlineCode parentName="p">{`dependencies`}</inlineCode>{` or in a level higher than us.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`dependencies: The dependencies of this dependency, exactly as at the top
level.`}</p>
      </li>
    </ul>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-shrinkwrap"
        }}>{`npm shrinkwrap`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/npm-shrinkwrap-json"
        }}>{`npm-shrinkwrap.json`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/configuring-npm/package-json"
        }}>{`package.json`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-install"
        }}>{`npm install`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      